/**
 * `/checkout`.
 *
 * ## O defeito que este arquivo conserta
 *
 * A cotacao era um `useMutation` **sem nenhum gatilho de montagem**: `cotacao.mutate()` so
 * aparecia em "tentar de novo" e em "conferir de novo". Como o esqueleto e desenhado enquanto
 * `cotando || cotacao === undefined`, a tela ficava para sempre em "Conferindo valores" e o
 * botao de confirmar nunca liberava. Nenhum `POST /v1/checkout/quote` saia. Disparada a mao, a
 * rota respondia `401` — porque ela e `SES` (`API §6.12`) e o app nunca pedia sessao. E, no modo
 * `DELIVERY`, o corpo levava `addressId: ''`, que `QuoteRequestSchema` (`Uuidv7Schema`) recusa
 * com `422`. Tres defeitos empilhados, e o primeiro escondia os outros dois.
 *
 * ## `useQuery`, e nao `useMutation` — e por que a justificativa antiga nao se sustentava
 *
 * O cabecalho anterior dizia: *"guardar o resultado numa chave de query seria guardar um preco
 * que vence — o oposto de `PWA §4.2 R12` (nunca em disco, em lugar nenhum)"*. R12 fala de
 * **disco**, e o que vai para o disco nao e decidido por ser query: e decidido pela allowlist
 * `PERSISTED_QUERY_KEYS` (`PWA §7.3`, `packages/api-client/src/persist/allowlist.ts`), que
 * **nao tem** `['checkout','quote']` — o padrao ali e nao persistir. E um `useMutation` guarda o
 * mesmo preco na mesma memoria (`cotacao.data` e lido logo abaixo, para montar o pedido): a
 * distincao nunca protegeu nada. O que resolve R12 e `staleTime: 0` + `gcTime: 0`, que estao
 * aqui e valem para as duas formas.
 *
 * O que decide de verdade e a natureza da chamada. Cotar e **leitura derivada do carrinho**:
 * `endpoints/orders.ts` registra que ela e a unica das rotas de escrita do fluxo **sem**
 * `Idempotency-Key`, e diz por que — *"cotar nao produz efeito: repetir cotacao gera outra
 * cotacao, o que e o comportamento desejado quando o preco mudou"*. Uma chamada segura de
 * repetir, cujo resultado e funcao de (carrinho, modalidade, endereco), e uma query. Com isso os
 * quatro requisitos saem do proprio hook, e nao de efeitos escritos a mao:
 *
 * | Requisito | Quem cumpre |
 * | --------- | ----------- |
 * | cotar ao entrar, sem apertar nada | o `useQuery` monta e busca |
 * | trocar modalidade/endereco recota | a chave carrega os dois |
 * | nao recotar em loop, nem duas vezes na montagem | deduplicacao por chave do TanStack Query |
 * | nao cotar quando a requisicao nao pode dar certo | `enabled` + opcoes que so existem completas |
 *
 * ## Onde entra a exigencia de sessao
 *
 * `API §6.12` marca `POST /v1/checkout/quote` como **`SES`** (a linha de `§6.10` diz "SES ou
 * anonimo", mas ela mesma se declara repeticao: *"Definida em §6.12"*). Tudo antes disso e
 * anonimo por contrato — catalogo, busca e o carrinho com `cartToken` (`API §6.9`, TTL 7 dias) —
 * e `POST /v1/cart/merge` existe exatamente para fundir o carrinho do visitante ao do titular no
 * login. Entao o ponto em que a sessao passa a ser obrigatoria **e a entrada do checkout**, e a
 * pergunta e feita aqui, antes de cotar, e nao no botao de confirmar (que precisaria de um total
 * que so a cotacao produz).
 *
 * Quem responde se ha sessao continua sendo o **servidor**, como manda `app/sessao.tsx`
 * (*"uma tela que confiasse neste booleano para liberar algo estaria fazendo autorizacao no
 * cliente"*): com o espelho em `DESCONHECIDA` — o estado de quem acabou de recarregar a pagina
 * com o cookie valido — a cotacao **sai**, e e o `401` que ensina o app. So depois de saber
 * (`ANONIMA`) e que a requisicao deixa de ser feita.
 */
import { temCodigo } from '@farmacia/api-client';
import { useApiClient, useApiQueries, type QueryOptions } from '@farmacia/api-client/query';
import type { Address, QuoteRequest, QuoteResponse } from '@farmacia/contracts';
import { toE164Br } from '@farmacia/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { descreverErro } from '../app/erros.js';
import { useSessao } from '../app/sessao.jsx';
import type { RascunhoDeEndereco } from '../entrega/EnderecoDeEntrega.jsx';
import { usePwa } from '../pwa/contexto.jsx';
import {
  Checkout,
  type BloqueioDoCheckout,
  type FormaDePagamento,
  type ModoDeEntrega,
} from '../telas/Checkout.jsx';

/**
 * DUVIDA: a versao vigente dos Termos e dado de instancia e deveria chegar em
 * `GET /v1/public/config`. Enquanto a rota nao existe no `@farmacia/api-client`, o valor fica
 * aqui, nomeado — e nao interpolado num objeto de 12 linhas onde ninguem o veria.
 */
const VERSAO_DOS_TERMOS = '1';

/**
 * A DUVIDA que este bloco carregava esta **fechada**.
 *
 * Ela dizia: *"o endereco de entrega nao tem de onde vir, e por isso `DELIVERY` nao fecha hoje
 * [...] `API §6.2` lista `GET /v1/me/addresses` e `POST /v1/me/addresses` como M (MVP), mas nao
 * ha rota implementada em `apps/api` nem schema em `@farmacia/contracts`"*. As tres pecas
 * passaram a existir — contrato, modulo e metodo do `@farmacia/api-client` —, e a constante
 * `ENDERECO_DE_ENTREGA = null` que ficava aqui saiu.
 *
 * O que a substitui e a agenda do titular (`queries.addresses.list()`), e a regra de qual
 * endereco vale por padrao **nao e escrita aqui**: `API §6.2` fixa *"no MVP o default e o
 * endereco mais recente"*, e a lista ja chega ordenada por isso. `data[0]` e o default, e por
 * isso nao ha campo `isDefault` a consultar nem escolha a repetir no cliente.
 */

export const Route = createFileRoute('/checkout')({ component: TelaDeCheckout });

/**
 * A opcao de reserva de quando a cotacao **nao pode** ser pedida — mesma forma do
 * `OPCOES_SEM_FILIAL` de `routes/busca.tsx`, e pelo mesmo motivo.
 *
 * O `useQuery` precisa de uma chave estavel mesmo desabilitado. Ela nunca e consultada, e o
 * `queryFn` **rejeita**: se algum dia alguem ligar o `enabled` sem completar os dados, o
 * resultado e um erro alto no proprio app, e nao um `401`/`422` do servidor exibido como
 * "Não conseguimos conferir os valores".
 */
const OPCOES_SEM_COTACAO: QueryOptions<QuoteResponse> = {
  queryKey: ['checkout', 'quote', 'incompleto'],
  queryFn: () =>
    Promise.reject(new Error('Cotacao sem carrinho, sem sessao ou sem endereco: nao deve sair.')),
  staleTime: 0,
  gcTime: 0,
};

function TelaDeCheckout(): ReactNode {
  const cliente = useApiClient();
  const queries = useApiQueries();
  const clienteDeQuery = useQueryClient();
  const navegar = useNavigate();
  const { conexao } = usePwa();
  const { estado: estadoDaSessao, definirEstado } = useSessao();

  /**
   * `null` = "a pessoa ainda nao escolheu". A modalidade efetiva cai no que o **carrinho** ja
   * declara (`CartResponse.fulfillmentMode`, `API §6.9`) — que e onde a intencao foi registrada.
   * Um `useState('DELIVERY')` fixo, como estava, escolhia por ela e escolhia justamente o modo
   * que hoje nao fecha.
   */
  const [modoEscolhido, setModoEscolhido] = useState<ModoDeEntrega | null>(null);
  const [pagamento, setPagamento] = useState<FormaDePagamento>('PIX');
  const [totalReconhecido, setTotalReconhecido] = useState<number | null>(null);

  const carrinho = useQuery(queries.cart.current());
  const modo: ModoDeEntrega = modoEscolhido ?? carrinho.data?.fulfillmentMode ?? 'PICKUP';

  /**
   * A agenda **so e buscada em `DELIVERY`**. `GET /v1/me/addresses` e `SES` e `no-store`: pedi-la
   * para quem vai retirar na loja gasta franquia de quem conta MB (`PWA §0.1`) e traz PII de
   * nivel F2 para a memoria de uma tela que nao a exibe.
   */
  const enderecos = useQuery({
    ...queries.addresses.list(),
    enabled: modo === 'DELIVERY' && estadoDaSessao !== 'ANONIMA',
  });

  const [enderecoEscolhido, setEnderecoEscolhido] = useState<string | null>(null);

  /**
   * Cidade e UF **nao sao perguntadas a pessoa** — vem da filial em que o carrinho esta.
   *
   * Uma farmacia de Solonopole entrega em Solonopole; pedir "cidade" e "UF" num formulario de
   * quatro campos e pedir que a pessoa redigite o que o produto ja sabe, e e mais uma chance de
   * errar. `GET /v1/stores` e publica e cacheada em borda (`API §8.1`), entao esta leitura nao
   * custa uma volta de rede no caminho critico.
   *
   * A alternativa correta seria `GET /v1/public/config` expondo o municipio do
   * `DeploymentProfile` — rota **prevista** e ainda inexistente. Enquanto ela nao existe, a
   * filial e a fonte mais proxima da verdade, e nao um palpite: e o endereco comercial que a
   * licenca sanitaria publica (`StoreAddressSchema`).
   */
  const lojas = useQuery({ ...queries.stores.list(), enabled: modo === 'DELIVERY' });
  const filialDoCarrinho = lojas.data?.data.find((loja) => loja.id === carrinho.data?.storeId);

  /**
   * As areas de entrega da filial — `GET /v1/delivery/zones?storeId=` (`API §6.10`, publica e
   * cacheada em borda por 10 min).
   *
   * Sem elas o formulario cadastrava endereco com `zoneId: null`, e a cotacao seguinte respondia
   * `OUT_OF_DELIVERY_AREA` — um endereco recem-criado para o qual a farmacia nao entrega. Foi o
   * navegador que mostrou isso: a suite inteira passava porque nenhum teste cotava de verdade.
   */
  const idDaFilial = carrinho.data?.storeId ?? null;
  const zonas = useQuery({
    ...queries.delivery.zones(idDaFilial ?? ''),
    enabled: modo === 'DELIVERY' && idDaFilial !== null,
  });

  const cadastroDeEndereco = useMutation({
    mutationFn: async (rascunho: RascunhoDeEndereco) => {
      const municipio = filialDoCarrinho?.address;
      if (municipio === undefined) {
        // Nao adivinha municipio. Sem a filial carregada, cadastrar produziria um endereco em
        // uma cidade inventada — e o entregador so descobriria isso na porta.
        throw new Error('Sem a filial do carrinho nao ha municipio para o endereco.');
      }
      return await cliente.addresses.create({
        // `zoneId` chega **sempre** preenchido: o formulario o exige. `?? {}` existe so para o
        // tipo, que o mantem anulavel porque `Address.zoneId` e anulavel no contrato.
        ...(rascunho.zoneId === null ? {} : { zoneId: rascunho.zoneId }),
        street: rascunho.street.trim(),
        ...(rascunho.number.trim() === '' ? {} : { number: rascunho.number.trim() }),
        reference: rascunho.reference,
        // Cidade e UF sao da INSTANCIA, e nao da pessoa: uma farmacia de Solonopole entrega em
        // Solonopole. O formulario nao os pergunta (ver `EnderecoDeEntrega.tsx`).
        city: municipio.city,
        uf: municipio.uf,
        recipientName: rascunho.recipientName.trim(),
        recipientPhone: toE164Br(rascunho.recipientPhone),
      });
    },
    retry: false,
    onSuccess: (criado) => {
      // Seleciona o que acabou de nascer. Sem isto, a lista recarregada poria em primeiro lugar
      // o mais recente — que **e** este —, mas so depois do `invalidate`; escolher agora evita a
      // piscada em que a tela mostra o endereco antigo marcado.
      setEnderecoEscolhido(criado.id);
      void clienteDeQuery.invalidateQueries({ queryKey: queries.addresses.list().queryKey });
    },
  });

  const cartId = carrinho.data?.id ?? null;
  const temItens = (carrinho.data?.items.length ?? 0) > 0;

  /**
   * A agenda como a TELA a ve: o que o servidor listou, mais o endereco que acabou de nascer.
   *
   * ## Por que o recem-criado entra aqui, e nao so depois do refetch
   *
   * `POST /v1/me/addresses` devolve o endereco inteiro — ele **e** verdade do servidor, ja
   * gravado. Esperar `GET /v1/me/addresses` confirmar o que o `POST` acabou de dizer significa
   * que, entre as duas respostas, a pessoa ve "endereco salvo" e a cotacao **nao sai**: a tela
   * fica em "Conferindo valores" por um endereco que existe. Numa rede de interior essa janela
   * nao e teorica, e se o refetch falhar ela nao fecha nunca.
   *
   * Foi exatamente isso que o caso "cadastrar o endereco pela tela faz a cotacao de ENTREGA
   * sair" pegou: a criacao acontecia, `setEnderecoEscolhido` guardava o id, e `enderecoAtual` o
   * descartava porque `agenda.some(...)` era falso — a lista ainda nao o tinha.
   *
   * O `filter` remove a duplicata quando o refetch chega: os dois passam a existir por um
   * instante, e o da mutacao vence por estar na frente.
   */
  const criado = cadastroDeEndereco.data;
  const agenda: readonly Address[] =
    criado === undefined
      ? (enderecos.data?.data ?? [])
      : [criado, ...(enderecos.data?.data ?? []).filter((item) => item.id !== criado.id)];

  /**
   * `null` = "ainda nao escolheu". O efetivo cai no **mais recente**, que `API §6.2` define como
   * o default do MVP — e a lista ja vem nessa ordem, entao `agenda[0]` e ele.
   *
   * O estado guarda a ESCOLHA, e nao o endereco efetivo: derivar o efetivo a cada render e o que
   * mantem os dois de acordo sem um `useEffect` que copie um no outro.
   */
  const enderecoAtual: string | null =
    enderecoEscolhido !== null && agenda.some((item) => item.id === enderecoEscolhido)
      ? enderecoEscolhido
      : (agenda[0]?.id ?? null);

  /**
   * O alvo da modalidade: a filial em `PICKUP`, o endereco em `DELIVERY`.
   *
   * Os dois sao `Uuidv7Schema` no contrato (`API §6.20.6`), e os dois entram na chave da query —
   * e por isso que trocar de modalidade ou de endereco recota, sem um unico efeito escrito.
   */
  const alvo: string | null =
    modo === 'PICKUP' ? (carrinho.data?.storeId ?? null) : enderecoAtual;

  function irParaEntrar(): void {
    // Volta para ca depois do OTP, e o carrinho anonimo e fundido no caminho
    // (`POST /v1/cart/merge`, em `routes/entrar.tsx`).
    void navegar({ to: '/entrar', search: { voltarPara: '/checkout' } });
  }

  function mudarModo(novo: ModoDeEntrega): void {
    setModoEscolhido(novo);
    // O total muda de propósito quando a modalidade muda (o frete entra ou sai). Exigir aceite
    // de um aumento que a propria pessoa pediu seria `PWA-043` aplicado onde ele nao protege
    // ninguem — e mostrar "o valor caiu" na volta seria falso.
    setTotalReconhecido(null);
  }

  /*
   * O bloqueio e resolvido na ordem em que a pessoa esbarra nele: sem sessao nao adianta ter
   * carrinho; sem itens nao adianta ter endereco. `DESCONHECIDA` **nao** bloqueia — ver o
   * cabecalho: quem recarregou a pagina com o cookie valido esta nesse estado, e negar a
   * cotacao ali mandaria para o login quem ja esta logado.
   */
  const bloqueio: BloqueioDoCheckout | undefined =
    estadoDaSessao === 'ANONIMA'
      ? { tipo: 'SEM_SESSAO', aoEntrar: irParaEntrar }
      : carrinho.data !== undefined && !temItens
        ? { tipo: 'SEM_ITENS', aoBuscar: () => void navegar({ to: '/busca' }) }
        : modo === 'DELIVERY' && alvo === null
          ? { tipo: 'SEM_ENDERECO', aoRetirarNaLoja: () => { mudarModo('PICKUP'); } }
          : undefined;

  const podeCotar = bloqueio === undefined && cartId !== null && alvo !== null && temItens;

  const corpo: QuoteRequest | null =
    cartId === null || alvo === null
      ? null
      : {
          cartId,
          fulfillment:
            modo === 'PICKUP'
              ? { mode: 'PICKUP', storeId: alvo }
              : { mode: 'DELIVERY', addressId: alvo },
        };

  /*
   * `paymentMethodHint` fica FORA de proposito. `API §6.20.6` escreve "hint nao vincula", e
   * `contracts/public/checkout.ts` registra que ela nao concede desconto no MVP: manda-la faria
   * cada toque no radio de pagamento recotar — dado da franquia de quem conta MB (`PWA §0.1`)
   * gasto para receber o mesmo total de volta. Se um dia houver preco por meio de pagamento, o
   * campo entra aqui **e** na chave, juntos.
   */
  const opcoes: QueryOptions<QuoteResponse> =
    corpo === null
      ? OPCOES_SEM_COTACAO
      : {
          // A chave e o corpo: dois checkouts com os mesmos dados sao a mesma cotacao, e mudar
          // qualquer um deles e uma cotacao nova. `cartVersion` entra porque mexer no carrinho
          // noutra aba invalida o preco que esta na tela.
          queryKey: [
            'checkout',
            'quote',
            corpo.cartId,
            carrinho.data?.version ?? 0,
            modo,
            alvo,
          ],
          queryFn: ({ signal }) => cliente.checkout.quote(corpo, signal),
          // `PWA §4.2 R12` em memoria: nada de cotacao sobrevive a tela que a pediu.
          staleTime: 0,
          gcTime: 0,
        };

  const cotacao = useQuery({
    ...opcoes,
    enabled: podeCotar,
    // O default do app e `retry: 1` (`app/providers.tsx`). Aqui ele custaria uma segunda
    // requisicao para receber o mesmo `401`/`422`, e atrasaria a tela que explica o que falta.
    retry: false,
  });

  /*
   * O servidor e quem sabe se ha sessao. Um `401` na cotacao corrige o espelho — e a tela deixa
   * de ser "Não conseguimos conferir os valores" (que nao diz o que fazer) para ser o convite de
   * entrar (que diz).
   */
  const erroDaCotacao = cotacao.error;
  useEffect(() => {
    if (descreverErroDeAutenticacao(erroDaCotacao)) definirEstado('ANONIMA');
  }, [erroDaCotacao, definirEstado]);

  const pedido = useMutation({
    mutationFn: () => {
      const atual = cotacao.data;
      if (atual === undefined) throw new Error('Sem cotacao: o botao de confirmar nao libera.');
      return cliente.orders.create({
        quoteId: atual.quoteId,
        quoteHash: atual.quoteHash,
        cartId: atual.cartId,
        // PWA-042 — **assercao do que o cliente viu**. So pode produzir `409 PRICE_CHANGED`;
        // nunca influencia o calculo do servidor, que recalcula de qualquer jeito
        // (`SECURITY §8.1`, regra 2: "o total e recalculado tres vezes").
        acknowledgedTotalCents: atual.totals.total.amountCents,
        // `OrderPaymentRequest` e uma uniao discriminada por `method`; `DINHEIRO_ENTREGA` pede
        // `changeForCents` (`null` = "nao preciso de troco", `API §6.20.7`).
        payment:
          pagamento === 'PIX'
            ? { method: 'PIX' }
            : { method: 'DINHEIRO_ENTREGA', changeForCents: null },
        // A versao dos Termos exibida no checkout, ecoada e gravada no snapshot do contrato.
        // DUVIDA: ela deveria vir de `GET /v1/public/config` (rota prevista, `API §6.5`), que
        // ainda nao existe no cliente. Ate la, a constante fica visivel aqui em vez de
        // escondida — um literal que o servidor recusa e melhor que um que ele aceita calado.
        acceptedPolicyVersion: VERSAO_DOS_TERMOS,
      });
    },
    retry: false,
    onSuccess: (resultado) => {
      void navegar({ to: '/pedidos/$id', params: { id: resultado.order.id } });
    },
  });

  const erroDoCadastro = descreverErro(cadastroDeEndereco.error);

  return (
    <Checkout
      modo={modo}
      aoMudarModo={mudarModo}
      pagamento={pagamento}
      aoMudarPagamento={setPagamento}
      cotacao={cotacao.data}
      cotando={cotacao.isFetching}
      cotacaoVencida={temCodigo(cotacao.error, 'QUOTE_EXPIRED')}
      // Nem cotacao vencida (que e fluxo) nem `401` (que virou o bloqueio de entrar) chegam como
      // erro. Todo o resto chega, com o codigo do catalogo. Antes, sumia.
      erro={
        temCodigo(cotacao.error, 'QUOTE_EXPIRED') || descreverErroDeAutenticacao(cotacao.error)
          ? null
          : (cotacao.error ?? pedido.error)
      }
      // "Tentar de novo" e "Conferir de novo" sao a mesma acao — refazer a cotacao —, e agora
      // ela e um `refetch`, nao um disparo manual do que deveria ter acontecido sozinho.
      aoTentarDeNovo={() => { void cotacao.refetch(); }}
      aoRecotar={() => {
        // `PWA-043`: o total que a pessoa esta vendo AGORA vira o total reconhecido antes de
        // pedir outro. Sem esta linha, uma cotacao nova mais cara passaria sem aceite, porque
        // nao haveria com o que compara-la.
        setTotalReconhecido(cotacao.data?.totals.total.amountCents ?? null);
        void cotacao.refetch();
      }}
      aoConfirmar={() => { pedido.mutate(); }}
      confirmando={pedido.isPending}
      offline={conexao === 'OFFLINE'}
      totalReconhecidoCents={totalReconhecido}
      aoReconhecerAumento={() => { setTotalReconhecido(cotacao.data?.totals.total.amountCents ?? null); }}
      bloqueio={bloqueio}
      {...(modo === 'DELIVERY'
        ? {
            entrega: {
              enderecos: agenda,
              zonas: zonas.data?.data ?? [],
              carregando: enderecos.isLoading,
              selecionado: enderecoAtual,
              aoSelecionar: setEnderecoEscolhido,
              aoCadastrar: (rascunho) => { cadastroDeEndereco.mutate(rascunho); },
              cadastrando: cadastroDeEndereco.isPending,
              // `descreverErro` devolve a frase do CATALOGO — o app nao escreve texto de erro
              // proprio (`API §2.1`, regra 2). `null` significa "nao houve erro", e vira
              // ausencia do campo em vez de string vazia.
              ...(erroDoCadastro === null ? {} : { erro: erroDoCadastro.mensagem }),
            },
          }
        : {})}
    />
  );
}

/**
 * O erro pede reautenticacao?
 *
 * Sao os tres codigos que o `API §2.2` marca com acao `AUTH`: `UNAUTHENTICATED` (a rota e `SES`
 * e nao havia sessao), `SESSION_REVOKED` (havia, e foi encerrada) e `STEP_UP_REQUIRED`.
 *
 * A lista e **explicita**, e nao derivada de `ApiError.action === 'AUTH'`, de proposito. O
 * catalogo esta fechado em 106 codigos (`API §10.3`) e mandar alguem para o login e uma acao
 * cara: se um codigo futuro ganhar `AUTH` por engano, a forma derivada passaria a expulsar
 * gente do checkout sem que uma linha de codigo tivesse mudado. `temCodigo` e tipado contra o
 * `ErrorCode`, entao um codigo inexistente aqui nao compila.
 */
function descreverErroDeAutenticacao(erro: unknown): boolean {
  return temCodigo(erro, 'UNAUTHENTICATED', 'SESSION_REVOKED', 'STEP_UP_REQUIRED');
}
